import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, useTheme, Divider } from '@ublend-npm/aulaui-core';
import { toggleStarSpace } from '@core/spaceSelector/operations';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { toggleWidgetPreference } from '@app/actions/home/widgetPreferences';
import { getHomeSpacesLoading, isHomeViewRoleReady } from '@app/selectors/home';
import { changeClassRoom } from '@core/actions/classRoom';
import { fetchRecentlyViewedSpaces } from '@core/spaceSelector/operations';

// Imports specific to the Recent Spaces Widget
import { NAVIGATION_SOURCE_RECENTLY_VIEWED_SPACES } from '@core/constants/analytics';
import { trackHomeViewAllClicked } from '../common';
import { getHomeRecentSpaces } from './getHomeRecentSpaces';
import SpaceItem from '../common/SpaceItem';
import { WidgetCard, WidgetErrorState } from '../common';
import { getStarredSpaceColors } from '../../colors';
import SpaceListSkeleton from '../common/SpaceListSkeleton'; // Reusing the skeleton
import RecentlyViewedSpacesEmptyState from './RecentlyViewedSpacesEmptyState';
import { push } from 'react-router-redux';
import { currentUser } from '@core/selectors/user';

import { useResponsiveSpacesNavigation } from '@app/hooks/useResponsiveSpacesNavigation';
import useTrackHomeWidgetLoaded from '../common/useTrackHomeWidgetLoaded';

const STRINGS = {
  emptyState: 'No recently viewed spaces.',
  title: 'Recently viewed spaces',
  hide: 'Hide',
  viewAll: 'View all recently viewed spaces',
  widgetKey: 'home-recently-viewed-spaces-widget',
};

const ITEM_RENDER_COUNT = 4;
const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'];

const RecentlyViewedSpacesWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();
  const { openSpacesList } = useResponsiveSpacesNavigation('recent');
  const user = useSelector(currentUser);
  const isGlobalStaff = user?.userRoles?.includes('staff') || false;

  const isSpacesLoading = useSelector(getHomeSpacesLoading);
  const isViewRoleReady = useSelector(isHomeViewRoleReady);
  const {
    isErrored,
    errorType: storedErrorType,
    status: recentSpacesStatus = 'idle',
  } = useSelector(
    (state: any) => state.spaceSelector?.recentlyViewedSpaces || {}
  );
  // recentlyViewedSpaces uses status ('idle'|'started'|'success'|'error'),
  // not an isLoading flag — treat idle/started as loading so we don't fire
  // HOME_WIDGET_LOADED with count 0 before the first fetch settles.
  const isRecentSpacesLoading =
    !isViewRoleReady ||
    recentSpacesStatus === 'idle' ||
    recentSpacesStatus === 'started';
  const isRecentSpacesSettled =
    isViewRoleReady &&
    (recentSpacesStatus === 'success' || recentSpacesStatus === 'error');
  const isLoading = isSpacesLoading || isRecentSpacesLoading;
  const errorType = isErrored ? storedErrorType || 'network' : null;

  const rawSpaces = useSelector(getHomeRecentSpaces) || [];

  React.useEffect(() => {
    // Wait for classRooms/mySpaces so isStaffAccess + role filtering are correct.
    if (!isViewRoleReady) {
      return;
    }
    dispatch(fetchRecentlyViewedSpaces());
  }, [dispatch, isViewRoleReady]);

  const spaces = useMemo(() => {
    return rawSpaces.map((s: any) => ({
      id: s.objectId || s.id,
      name: s.name,
      startDate: s.startDate,
      endDate: s.endDate,
      isStarred: s.isStarred || false,
      lastNavigation: s.lastNavigation,
      avatar: s.avatar || null,
      isStaffAccess: s.isStaffAccess || false,
      isAssigned: s.isAssigned,
      archived: s.archived || false,
    }));
  }, [rawSpaces]);

  const handleHideWidget = useCallback(() => {
    dispatch(toggleWidgetPreference(STRINGS.widgetKey, true));
  }, [dispatch]);

  const handleStarToggle = useCallback(
    (id: string) => {
      dispatch(toggleStarSpace({ spaceId: id }));
    },
    [dispatch]
  );

  const handleSpaceClick = useCallback(
    async (id: string) => {
      const space = spaces.find((s) => s.id === id);
      if (space?.lastNavigation) {
        let navigationUrl = space.lastNavigation.replace(/#/, '');
        await dispatch(changeClassRoom(id) as any);
        const [pathname, search] = navigationUrl.split('?');
        const queryParams = new URLSearchParams(search || '');
        queryParams.set('from', 'home');

        dispatch(
          push({
            pathname,
            search: `?${queryParams.toString()}`,
            state: { fromHome: true },
          })
        );
      } else {
        dispatch(
          push({
            pathname: `/dashboard/${id}/myspacenav`,
            search: '?from=home',
            state: { fromHome: true },
          })
        );
      }
    },
    [spaces, dispatch]
  );

  const handleRetry = useCallback(() => {
    dispatch(fetchRecentlyViewedSpaces() as any);
  }, [dispatch]);

  const content = useMemo(() => {
    if (isLoading) {
      return SKELETON_KEYS.map((key, index) => (
        <React.Fragment key={key}>
          <SpaceListSkeleton />
          {index < 2 && <Divider />}
        </React.Fragment>
      ));
    }

    if (errorType) {
      return (
        <WidgetErrorState
          type={errorType === 'network' ? 'offline' : errorType}
          onRetry={handleRetry}
          isRetrying={isLoading}
        />
      );
    }

    if (spaces.length === 0) {
      return <RecentlyViewedSpacesEmptyState />;
    }

    const visibleSpaces = spaces.slice(0, ITEM_RENDER_COUNT);

    return visibleSpaces.map((space, index) => (
      <React.Fragment key={space.id}>
        <SpaceItem
          space={space}
          isStarred={space.isStarred}
          onStarToggle={handleStarToggle}
          onClick={handleSpaceClick}
          isStaffAccess={space.isStaffAccess}
          isStaff={isGlobalStaff}
          archived={space.archived}
          isAssigned={space.isAssigned}
        />
        {index < visibleSpaces.length - 1 && <Divider />}
      </React.Fragment>
    ));
  }, [
    isLoading,
    errorType,
    spaces,
    handleStarToggle,
    handleSpaceClick,
    handleRetry,
  ]);

  useTrackHomeWidgetLoaded(
    'recently_viewed_spaces',
    isRecentSpacesSettled && !errorType,
    spaces.length
  );

  const menuActions = [{ label: STRINGS.hide, onClick: handleHideWidget }];

  const footer = (
    <Button
      fullWidth
      kind="secondary"
      endIcon={<ArrowForwardIcon />}
      sx={{
        width: '100%',
        color: getStarredSpaceColors(palette).starIcon,
      }}
      onClick={() => {
        trackHomeViewAllClicked(
          NAVIGATION_SOURCE_RECENTLY_VIEWED_SPACES,
          spaces.length
        );
        openSpacesList('recent');
      }}
    >
      {STRINGS.viewAll}
    </Button>
  );

  const footerUI =
    !isLoading && !errorType && spaces.length > ITEM_RENDER_COUNT
      ? footer
      : null;

  return (
    <WidgetCard
      title={STRINGS.title}
      menuActions={menuActions}
      footer={footerUI}
    >
      {content}
    </WidgetCard>
  );
};

export default RecentlyViewedSpacesWidget;
